/**
 * Formatting Utility Functions
 *
 * Purpose: Centralized formatting functions for the OrchestrateHQ AI frontend
 *          including currency, numbers, text display, and data formatting.
 *
 * Architecture: Part of the frontend utilities module, used across all components
 *              for consistent data display and formatting.
 *
 * Dependencies: utils/locale (the active locale), utils/datetime (date presets)
 * Author: Development Team
 * Created: 2025-01-27
 */

import { DEFAULT_LOCALE, getActiveLocale } from './locale';
import { formatDate as formatDateForDisplay, formatDateTime } from './datetime';

/** Default currency used when no code has been resolved yet. */
export const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * The locale every formatter defaults to is the signed-in user's (ORCH-191): resolved
 * server-side from their profile (Locale override → Home Country's locale → en-US)
 * and held in `utils/locale.ts`. Pass `locale` explicitly only to format for someone
 * else. `DEFAULT_LOCALE` is re-exported for callers that need the fallback itself.
 */
export { DEFAULT_LOCALE };

/** The two separators a locale uses to write a number ("," and "." for en-US). */
export interface NumberSeparators {
  group: string;
  decimal: string;
}

/**
 * Resolve the thousands and decimal separators for a locale
 *
 * Purpose: Single source for locale separators so input masks and parsers never
 *          hard-code "," and ".".
 * Logic: Reads the `group` and `decimal` parts Intl emits for a sample number.
 *        Falls back to en-US separators for an unknown locale.
 *
 * @param locale - BCP 47 locale tag (default: the active locale)
 * @returns The locale's group and decimal separators
 */
export const getNumberSeparators = (locale: string = getActiveLocale()): NumberSeparators => {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    return {
      group: parts.find((part) => part.type === 'group')?.value ?? ',',
      decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    };
  } catch {
    return { group: ',', decimal: '.' };
  }
};

/** Longest run of integer digits the input mask keeps — stays inside Number's exact range. */
const MAX_INTEGER_DIGITS = 15;

/**
 * Format a number with thousands separators and at most N decimals
 *
 * Purpose: The display form of a stored number in inputs, cards and labels —
 *          grouped ("1,234,567.5") with trailing zeros dropped, mirroring
 *          Excel's "General" format. `formatNumber` pads to a fixed decimal
 *          count instead, which is what tables and totals want.
 *
 * @param value - Number to format
 * @param maxDecimals - Upper bound on fraction digits (default: 2)
 * @param locale - Locale for separators (default: the active locale)
 * @returns Grouped string, or '' for a non-number
 */
export const formatGroupedNumber = (
  value: number,
  maxDecimals: number = 2,
  locale: string = getActiveLocale()
): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
      useGrouping: true,
    }).format(value);
  } catch (error) {
    const fixed = value.toFixed(maxDecimals);
    return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
  }
};

/**
 * Parse user-typed numeric text in a locale back to a number
 *
 * Purpose: The inverse of the input mask — strips group separators and converts
 *          the locale decimal separator so the API receives a plain number.
 * Logic: Removes the group separator (and any whitespace variant of it), swaps
 *        the decimal separator for '.', drops anything that is not a digit,
 *        '.', or '-', then parses.
 *
 * @param input - Text as typed, e.g. "1,250,000.5"
 * @param locale - Locale the text was written in (default: the active locale)
 * @returns The number, or null when the text holds no digits
 */
export const parseLocaleNumber = (
  input: string | null | undefined,
  locale: string = getActiveLocale()
): number | null => {
  if (typeof input !== 'string') {
    return null;
  }
  const { group, decimal } = getNumberSeparators(locale);

  let text = input.trim().split(group).join('');
  // Some locales group with a (narrow) no-break space; accept a plain space too.
  text = text.replace(/[\s\u00a0\u202f]/g, '');
  if (decimal !== '.') {
    text = text.split(decimal).join('.');
  }
  const normalized = text.replace(/[^0-9.-]/g, '');
  if (!/\d/.test(normalized)) {
    return null;
  }
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Apply a thousands-separator mask to text while it is being typed
 *
 * Purpose: Regroup the integer part of an in-progress entry on every keystroke
 *          ("1000000" → "1,000,000") without disturbing what the user is still
 *          typing — a trailing decimal separator or trailing fraction zeros
 *          ("1,000." / "1,000.50") are kept exactly as entered.
 * Logic: Splits on the locale decimal separator, keeps only digits on each
 *        side, caps the fraction at `maxDecimals`, regroups the integer digits
 *        via Intl, then reassembles with the sign.
 *
 * @param raw - Current input text, separators and all
 * @param maxDecimals - Fraction digits allowed while typing; 0 drops the decimal
 *                      part, the default leaves the fraction uncapped so a user
 *                      can over-type precision that is rounded on blur
 * @param locale - Locale for separators (default: the active locale)
 * @returns The masked text; '' when there is nothing numeric
 */
export const formatNumberInput = (
  raw: string,
  maxDecimals: number = Infinity,
  locale: string = getActiveLocale()
): string => {
  if (!raw) {
    return '';
  }
  const { decimal } = getNumberSeparators(locale);
  const trimmed = raw.trim();
  const negative = trimmed.startsWith('-');

  // Split at the first decimal separator; at zero precision the fraction is discarded.
  const decimalIndex = trimmed.indexOf(decimal);
  const integerText = decimalIndex >= 0 ? trimmed.slice(0, decimalIndex) : trimmed;
  const fractionText =
    decimalIndex >= 0 && maxDecimals > 0 ? trimmed.slice(decimalIndex + decimal.length) : null;

  const integerDigits = integerText
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '')
    .slice(0, MAX_INTEGER_DIGITS);
  const fractionDigits =
    fractionText === null ? null : fractionText.replace(/\D/g, '').slice(0, maxDecimals);

  if (!integerDigits && fractionDigits === null) {
    return negative ? '-' : '';
  }

  const groupedInteger = formatGroupedNumber(Number(integerDigits || '0'), 0, locale);
  const fraction = fractionDigits === null ? '' : `${decimal}${fractionDigits}`;
  return `${negative ? '-' : ''}${groupedInteger}${fraction}`;
};

/**
 * Resolve the display symbol for an ISO 4217 currency code
 *
 * Purpose: Single place that maps a currency code to the symbol shown in the UI
 *          (input adornments, cells, labels), so no caller keeps its own symbol map.
 * Logic: Asks `Intl.NumberFormat` for the currency part of a formatted zero, which
 *        gives the locale-correct symbol ('£' for GBP, '€' for EUR, '$' for USD) and
 *        falls back to the code itself for currencies with no distinct symbol. No code,
 *        or a code Intl can't parse, yields '' — the symbol comes only from the data's
 *        currency, never from a default (ORCH-423).
 *
 * @param currencyCode - ISO 4217 code (e.g., 'GBP'); nullish or empty means "no currency"
 * @param locale - Locale for symbol selection (default: the active locale)
 * @returns Display symbol (e.g., '£'), or '' when there is no currency to show
 */
export const getCurrencySymbol = (
  currencyCode?: string | null,
  locale: string = getActiveLocale()
): string => {
  if (!currencyCode) return '';

  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? ''
    );
  } catch {
    // Invalid code (Intl throws RangeError) — show no symbol rather than guess one.
    return '';
  }
};

/**
 * Format currency amount with symbol and decimals
 *
 * Purpose: Converts numeric amounts to properly formatted currency strings
 * Logic: Rounds to 2 decimal places and adds currency symbol
 *
 * @param amount - Amount to format
 * @param currencyCode - Currency code (default: 'USD'). The data's currency, never the locale's.
 * @param locale - Locale for formatting (default: the active locale)
 * @param digits - Fraction digit bounds (default: exactly 2)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currencyCode: string = DEFAULT_CURRENCY_CODE,
  locale: string = getActiveLocale(),
  digits: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'> = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }
): string => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '';
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      ...digits,
    }).format(amount);
  } catch (error) {
    // Fallback formatting — symbol resolution stays centralized in getCurrencySymbol
    return `${getCurrencySymbol(currencyCode, locale)}${amount.toFixed(digits.maximumFractionDigits ?? 2)}`;
  }
};

/**
 * Format number with appropriate separators and decimals
 *
 * Purpose: Converts numbers to readable format with proper separators
 * Logic: Uses Intl.NumberFormat for locale-specific formatting
 *
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 0)
 * @param locale - Locale for formatting (default: the active locale)
 * @param digits - Optional lower bound override (e.g. `minimumFractionDigits: 0` drops trailing zeros)
 * @returns Formatted number string
 */
export const formatNumber = (
  value: number,
  decimals: number = 0,
  locale: string = getActiveLocale(),
  digits: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits'> = {}
): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      ...digits,
    }).format(value);
  } catch (error) {
    return value.toFixed(decimals);
  }
};

/**
 * Format a fraction as a percentage
 *
 * Purpose: Converts a stored fraction (0.45) to its percentage display (45%)
 * Logic: `Intl`'s percent style does the ×100 and places the sign the way the
 *        locale writes it ('45,0 %' in de-DE, '45.0%' in en-US).
 *
 * @param value - Fraction (0-1) to convert to percentage
 * @param decimals - Number of decimal places (default: 1)
 * @param locale - Locale for formatting (default: the active locale)
 * @returns Formatted percentage string
 */
export const formatPercentage = (
  value: number,
  decimals: number = 1,
  locale: string = getActiveLocale()
): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '';
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch (error) {
    return `${(value * 100).toFixed(decimals)}%`;
  }
};

/**
 * Format file size in human-readable format
 *
 * Purpose: Converts bytes to readable file size (KB, MB, GB, etc.)
 * Logic: Converts to appropriate unit based on size
 *
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
};

/**
 * Format duration in human-readable format
 *
 * Purpose: Converts seconds to readable duration (hours, minutes, seconds)
 * Logic: Breaks down into appropriate time units
 *
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
};

/**
 * Format phone number with proper formatting
 *
 * Purpose: Formats phone numbers for display
 * Logic: Adds appropriate separators based on number length
 *
 * @param phone - Phone number string
 * @param countryCode - Country code for formatting (default: 'US')
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (phone: string, countryCode: string = 'US'): string => {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  if (countryCode === 'US') {
    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    } else if (digitsOnly.length === 11 && digitsOnly[0] === '1') {
      return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
    }
  }

  // Generic formatting for other countries
  if (digitsOnly.length >= 7 && digitsOnly.length <= 15) {
    return digitsOnly.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }

  return phone;
};

/**
 * Format credit card number with masking
 *
 * Purpose: Displays credit card numbers securely
 * Logic: Shows only last 4 digits, masks the rest
 *
 * @param cardNumber - Credit card number string
 * @returns Masked credit card number string
 */
export const formatCreditCard = (cardNumber: string): string => {
  if (!cardNumber || typeof cardNumber !== 'string') {
    return '';
  }

  // Remove all non-digit characters
  const digitsOnly = cardNumber.replace(/\D/g, '');

  if (digitsOnly.length < 4) {
    return cardNumber;
  }

  const lastFour = digitsOnly.slice(-4);
  const masked = '*'.repeat(digitsOnly.length - 4);

  return `${masked}${lastFour}`;
};

/**
 * Format social security number with masking
 *
 * Purpose: Displays SSN securely
 * Logic: Shows only last 4 digits, masks the rest
 *
 * @param ssn - Social security number string
 * @returns Masked SSN string
 */
export const formatSSN = (ssn: string): string => {
  if (!ssn || typeof ssn !== 'string') {
    return '';
  }

  // Remove all non-digit characters
  const digitsOnly = ssn.replace(/\D/g, '');

  if (digitsOnly.length !== 9) {
    return ssn;
  }

  return `***-**-${digitsOnly.slice(-4)}`;
};

/**
 * Format text with proper capitalization
 *
 * Purpose: Converts text to title case or sentence case
 * Logic: Capitalizes appropriate words based on case type
 *
 * @param text - Text to format
 * @param caseType - Type of case formatting (default: 'title')
 * @returns Formatted text string
 */
export const formatTextCase = (text: string, caseType: 'title' | 'sentence' = 'title'): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  if (caseType === 'title') {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  } else if (caseType === 'sentence') {
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  return text;
};

/**
 * Format address for display
 *
 * Purpose: Formats address components into readable format
 * Logic: Combines address parts with proper separators
 *
 * @param address - Address object with components
 * @returns Formatted address string
 */
export const formatAddress = (address: {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}): string => {
  const parts: string[] = [];

  if (address.street) parts.push(address.street);
  if (address.city && address.state) {
    parts.push(`${address.city}, ${address.state}`);
  } else if (address.city) {
    parts.push(address.city);
  } else if (address.state) {
    parts.push(address.state);
  }
  if (address.zipCode) parts.push(address.zipCode);
  if (address.country) parts.push(address.country);

  return parts.join(', ');
};

/**
 * Format relative time (e.g., "2 hours ago")
 *
 * Purpose: Provides human-readable relative time for better UX
 * Logic: Calculates time difference and formats accordingly
 *
 * @param date - Date to format relative to now
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(targetDate.getTime())) {
    return 'Invalid date';
  }

  const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    return formatDateForDisplay(targetDate);
  }
};

/**
 * Format audit timestamp for display (Created/Modified fields)
 *
 * Purpose: Formats timestamps to include both date and time in the browser's timezone
 * Logic: A thin wrapper over `formatDateTime`'s medium date + short time presets, so
 *        audit columns follow the user's locale and clock ("Jan 27, 2025, 2:30 PM" in
 *        en-US, "27.01.2025, 14:30" in de-DE) like every other datetime.
 *
 * @param timestamp - ISO timestamp string or Date object
 * @returns Formatted datetime string, 'Missing Date' or 'Invalid Date'
 */
export const formatAuditTimestamp = (timestamp: string | Date): string => {
  if (!timestamp) {
    return 'Missing Date';
  }

  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  return formatDateTime(date, 'medium', 'short');
};

/**
 * Format user name for audit fields (Created By/Modified By)
 *
 * Purpose: Formats user information for display in audit fields
 * Logic: Handles system users, missing users, and displays full names
 *
 * Display Rules:
 * - System UUID (00000000-0000-0000-0000-000000000001) → "System User"
 * - Empty GUID (00000000-0000-0000-0000-000000000000) → "Missing User"
 * - Null/undefined → "Missing User"
 * - Valid user ID with user data → "Firstname Lastname" or email
 * - Valid user ID without user data → "Missing User"
 *
 * @param userId - User ID (UUID string)
 * @param userData - Optional user data object with name information
 * @returns Formatted user name string
 */
export const formatAuditUser = (
  userId: string | null | undefined,
  userData?: { first_name?: string; last_name?: string; email?: string }
): string => {
  // Handle null/undefined user ID - show as Missing User
  if (!userId) {
    return 'Missing User';
  }

  // Handle system user (system UUID) - show as System User
  if (userId === '00000000-0000-0000-0000-000000000001') {
    return 'System User';
  }

  // Handle empty GUID - show as Missing User
  if (userId === '00000000-0000-0000-0000-000000000000') {
    return 'Missing User';
  }

  // If we have user data with names, format as "Firstname Lastname"
  if (userData?.first_name || userData?.last_name) {
    const firstName = userData.first_name || '';
    const lastName = userData.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();

    if (fullName) {
      return fullName;
    }
  }

  // Fallback to email if available
  if (userData?.email) {
    return userData.email;
  }

  // If we have a user ID but no user data, indicate missing user data
  return 'Missing User';
};
